import Styles from './SecurityTraining.module.scss';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
//Views
import { InstructionView } from './InstructionView/InstructionView';
import { ModuleView } from './ModuleView/ModuleView';
import { SimulationView } from './SimulationView/SimulationView';
import { QuizView } from './QuizView/QuizView';
import { PasswordStrength } from './PasswordStrength/PasswordStrength';
import { NodeTimeline } from 'components/global/NodeLayout/NodeTimeline';
import { useAPI } from 'components/lib';
import { decoder } from 'utils/helpers/decoder';
import axios from 'axios';
import { ConfirmationCard } from 'components/global/ConfirmationCard/ConfirmationCard';

//icons
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { BackConfirmationCard } from 'components/global/BackConfirmationCard/BackConfirmationCard';




export function SecurityTraining(props) {

    const { lab_id, node_id } = useParams();
    const { resource } = useParams();

    const { data, loading } = useAPI(`/api/labs/${lab_id}/nodes/${node_id}/${resource}`, 'GET');

    const [steps, setSteps] = useState();
    const [nodeFinished, setNodeFinished] = useState(false);
    const [nodeInfo, setNodeInfo] = useState();
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState({ simulation: [], quiz: [] })
    const [isMuted, setIsMuted] = useState(true);
    const [showResults, setShowResults] = useState(false);
    const [disableNext, setDisableNext] = useState(false);
    const [startLab, setStartLab] = useState(true);
    const roundedPercentage = nodeFinished ? 100 : Math.ceil(currentStep / nodeInfo?.numOfSteps * 100);
    const navigate = useNavigate();
    const currentRef = useRef(null);
    const [maxUnlocked, setMaxUnlocked] = useState(0);

    useEffect(() => {
        setMaxUnlocked(prev => Math.max(prev, currentStep));
    }, [currentStep]); 

    useEffect(() => {
        if (steps && steps[currentStep]?.hasExercise) {
            setDisableNext(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, steps])

    useEffect(() => {
        if (data) {
            const jsonData = decoder(data);
            setNodeInfo(jsonData);
            setSteps(jsonData.steps.steps);
            jsonData.numOfSteps = jsonData.steps.steps.length;

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (currentStep === nodeInfo?.numOfSteps - 1) {
            setNodeFinished(true)
        } else {
            setNodeFinished(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep])

    //audio controls
    useEffect(() => {
        if (steps) {
            const audioLink = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}${steps[currentStep]?.voice}`;
            if (!audioLink) return;
            const audioElement = new Audio(audioLink);

            if (!isMuted) {
                audioElement.play();
            }
            return () => {
                audioElement.pause();
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMuted, currentStep]);

    const handleVoiceClick = () => {
        setIsMuted(!isMuted);
    };

    function handleNextClick() {
        if (currentStep === nodeInfo.numOfSteps - 1) {
            setNodeFinished(true)
            return;
        } else {
            setCurrentStep(currentStep + 1);
        }


    }

    /**function to select which view to be used */
    function viewSelector(data) {
        switch (data?.type) {
            case 'InstructionView':
                return (
                    <InstructionView
                        data={data}
                        layout={data?.layout}
                    />
                );
            case 'ModuleView':
                return (
                    <ModuleView
                        data={data}
                    />
                );
            case 'SimulationView':
                return (
                    <SimulationView
                        data={data}
                        setAnswers={setAnswers}
                        answers={answers}
                        onComplete={handleNextClick}
                        showResults={showResults}
                        setShowResults={setShowResults}
                        nextDisabled={() => { setDisableNext(!disableNext) }}

                    />
                );
            case 'QuizView':
                return (
                    <QuizView
                        data={data}
                        onFinish={handleNextClick}
                        currentStep={currentStep}
                        setCurrentStep={setCurrentStep}
                        setQuiz={setAnswers}
                    />
                );
            case 'PasswordStrength':
                return (
                    <PasswordStrength
                        data={data}
                        nextDisabled={() => { setDisableNext(!disableNext) }}

                    />
                );
            default:
                navigate('/notfound');

        }
    }

    function handleFinish() {
        const options = {
            method: 'POST',
            url: `/api/user/labs/${lab_id}/nodes/${node_id}/end`,
            headers: {
                accept: 'application/json',
            },
            ...(nodeInfo.is_assessment === 1 && { data: { completionPercent: roundedPercentage } }),
        };
        axios
            .request(options)
            .then(async function (response) {
                await new Promise((resolve) => setTimeout(resolve, 50)); // Adding a wait for api post to happen
                if (nodeInfo?.is_assessment) {
                    navigate(`/labs/${lab_id}/nodes`);
                } else {
                    navigate(`/labs/${lab_id}/nodes/${node_id}/quiz/attempt`);
                }
            })
            .catch((err) => {
                navigate('/oops');
            });
    }
    function handlePrevClick() {
        setDisableNext(false);
        setCurrentStep(currentStep - 1)
    }

    async function startLabClick() {
        const options = {
            method: 'POST',
            url: `/api/user/labs/${lab_id}/nodes/${node_id}/start`,
            headers: {
                accept: 'application/json',
            },
        };
        await axios
            .request(options)
            .then((response) => {
                new Promise((resolve) => setTimeout(resolve, 100));
                setStartLab(false);
            })
            .catch((err) => {
                if (err.response.status === 403) {
                    navigate('/nopermission');
                } else {
                    navigate('/oops');
                }
            });
    }
    const navigateBack = () => {
        navigate(-1);
    };

    return (
        loading ?
            <LoadingBackdrop isOpen={true} />
            : steps && (
                <>	
                    <BackConfirmationCard
                        isSaveAvailable={false}
                    />
                
                <div id='ancestorContainer' className={Styles.ancestorContainer}>
                    <span className={`${Styles.contentContainer}`}>
                        <ConfirmationCard
                            title={nodeInfo.is_assessment ? 'Start Assessment?' : 'Start Course?'}
                            body={'Press Confirm to Start Course.'}
                            showCard={startLab}
                            setShowCard={setStartLab}
                            handleCancel={navigateBack}
                            handleConfirm={startLabClick}
                        />
                        <p id='title' className={Styles.titleStyles}>
                            {nodeInfo?.name}
                        </p>
                        <div id='videoContainer' className={Styles.videoContainer}>
                            {
                                steps[currentStep]?.voice &&
                                <button onClick={handleVoiceClick} className={Styles.muteBtn}>
                                    {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                                </button>
                            }
                            {viewSelector(steps[currentStep])}

                        </div>
                        <div id='buttonContainer' className={Styles.buttonContainer}>
                            <button
                                id='nextButton'
                                className={Styles.nextButton}
                                disabled={currentStep === 0}
                                onClick={handlePrevClick}
                            >
                                Previous Step
                            </button>
                            <button onClick={() => { setCurrentStep(0) }} id='nextButton' className={Styles.nextButton}>
                                Restart
                            </button>
                            <AivagamButton
                                id='nextButton'
                                classStyles={Styles.nextButton}
                                buttonText={nodeFinished ? 'Finish' : 'Next'}
                                type={nodeFinished && 'action'}
                                disabled={disableNext}
                                onClick={nodeFinished ? handleFinish : handleNextClick}
                            >
                                Next Step
                            </AivagamButton>

                        </div>
                    </span>
                    <NodeTimeline
                        nodeFinished={nodeFinished}
                        currentStep={currentStep}
                        steps={steps}
                        currentRef={currentRef}
                        hideToolTip={true}
                        setCurrentStep = {setCurrentStep}
                        maxUnlocked = {maxUnlocked}
                    />
                </div>
                </>
            )
    );
}